import React, { useState, createContext, useContext } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './ExternalLinkWarning.css';

const ExternalLinkContext = createContext();

export const useExternalLink = () => {
  const context = useContext(ExternalLinkContext);
  if (!context) {
    throw new Error('useExternalLink must be used within ExternalLinkProvider');
  }
  return context;
};

// Helper function to check if URL is part of MLWB (should not show warning)
const isMLWBLink = (url) => {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    return hostname.includes('mlwb.in') || hostname.includes('mlwb.gov.in');
  } catch (e) {
    return false;
  }
};

export const ExternalLinkProvider = ({ children }) => {
  const [warningState, setWarningState] = useState({
    isOpen: false,
    url: null
  });

  const showWarning = (url) => {
    // Don't show warning for MLWB internal links
    if (isMLWBLink(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setWarningState({ isOpen: true, url });
  };

  const closeWarning = () => {
    setWarningState({ isOpen: false, url: null });
  };

  const proceedToExternalLink = () => {
    if (warningState.url) {
      window.open(warningState.url, '_blank', 'noopener,noreferrer');
      closeWarning();
    }
  };

  return (
    <ExternalLinkContext.Provider value={{ showWarning }}>
      {children}
      <ExternalLinkWarningModal
        isOpen={warningState.isOpen}
        onClose={closeWarning}
        onProceed={proceedToExternalLink}
      />
    </ExternalLinkContext.Provider>
  );
};

const ExternalLinkWarningModal = ({ isOpen, onClose, onProceed }) => {
  const { isMarathi } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="external-link-warning-overlay" onClick={onClose}>
      <div className="external-link-warning-modal" onClick={(e) => e.stopPropagation()}>
        <div className="external-link-warning-content">
          <div className="external-link-warning-icon">
            <svg 
              width="48" 
              height="48" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <div className="external-link-warning-text">
            <p>
              {isMarathi() 
                ? <>तुम्ही बाह्य वेबसाइटवर पुनर्निर्देशित केले जात आहात. कृपया लक्षात घ्या की <strong>महाराष्ट्र कामगार कल्याण मंडळ</strong> पोर्टल बाह्य वेबसाइट्सच्या सामग्री आणि गोपनीयता धोरणांसाठी जबाबदार नाही.</>
                : <>You are being redirected to an external website. Please note that <strong>Department of Maharashtra Labour Welfare Board</strong> Portal is not responsible for external websites content & privacy policies.</>
              }
            </p>
          </div>
        </div>
        <div className="external-link-warning-actions">
          <button 
            className="external-link-warning-btn external-link-warning-btn-close"
            onClick={onClose}
          >
            {isMarathi() ? 'बंद करा' : 'Close'}
          </button>
          <button 
            className="external-link-warning-btn external-link-warning-btn-proceed"
            onClick={onProceed}
          >
            {isMarathi() ? 'वेबसाइटवर जा' : 'Go to website'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExternalLinkWarningModal;

